import React, { useRef, useState, useEffect, useCallback } from "react";
import { FaQuestionCircle } from 'react-icons/fa';
import { BookingFormProps } from '../../types/types';
import {useTranslation} from "react-i18next";
import { getRouteCost } from '../../services/orderService';
import { GBACity } from "../../utils/Constants";

const BookingForm: React.FC<BookingFormProps> = React.memo(({
  onOrderCreate,
  onPickupTimeChange,
  onPassengersChange,
  onLuggageChange,
  onPickupTimeErrorChange,
  pickupTime,
  pickupTimeError,
  passengers,
  luggage,
  orderCreated,
  onConfirmOrder,
  onCancelOrder,
  mileage,
}) => {
  const { t } = useTranslation();
  const [location, setLocation] = useState("");
  const [destination, setDestination] = useState("");

  const [locationCity, setLocationCity] = useState<GBACity>(GBACity.HONG_KONG);
  const [destinationCity, setDestinationCity] = useState<GBACity>(GBACity.SHENZHEN);


  const [showLocationTooltip, setShowLocationTooltip] = useState(false);

  const [showDestinationTooltip, setShowDestinationTooltip] = useState(false);
  const tooltipRef = useRef(null);

  const [locationEmpty, setLocationEmpty] = useState(false);
  const [destinationEmpty, setDestinationEmpty] = useState(false);

  const [loadingCreateOrder, setLoadingCreateOrder] = useState(false);
  const prevOrderCreatedRef = useRef(false);

  const [showHKD, setShowHKD] = useState(true);  // True for showing rate in HKD, False for showing rate in CNY
  const [rates, setRates] = useState<{ HKD: number | null, CNY: number | null, rate: number | null }>({
    HKD: null,
    CNY: null,
    rate: null
  });

  useEffect(() => {
    // Only reset location/destination when order status changes from created to not created
    if (!orderCreated && prevOrderCreatedRef.current) {
      setLocation("");
      setDestination("");
    } else if (orderCreated && !prevOrderCreatedRef.current) {
      const getCostData = async () => {
        try {
          const costData = await getRouteCost({orderDateTime: pickupTime, mileage: mileage});
          if (costData) {
            setRates({
              HKD: costData.HKD,
              CNY: costData.CNY,
              rate: costData.rate
            });
          }
        } catch (error) {
          console.error("Failed to fetch cost data:", error);
        }
      };

      getCostData();
    }

    // Update ref with current value for next render comparison
    prevOrderCreatedRef.current = orderCreated;
  }, [orderCreated, pickupTime, mileage]);

  const handleCreateOrder = useCallback(async () => {
    setLoadingCreateOrder(true);

    if (new Date(pickupTime) < new Date()) {
      onPickupTimeErrorChange(t('BookingPickupTimeError'));
    } else {
      onPickupTimeErrorChange("");
    }

    if (!location) {
      setLocationEmpty(true);
    } else {
      setLocationEmpty(false);
    }

    if (!destination) {
      setDestinationEmpty(true);
    } else {
      setDestinationEmpty(false);
    }


    if (!location || !destination) {
      setLoadingCreateOrder(false);
      return;
    }
    const data = await onOrderCreate(location, destination, locationCity, destinationCity);
    if (!data) {
      setLocation("");
      setDestination("");
    } else {
      setLocation(data.location.name);
      setDestination(data.destination.name);
    }
    setLoadingCreateOrder(false);
  }, [location, destination, onOrderCreate, pickupTime, pickupTimeError]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col">
        <div className="grid grid-cols-12 gap-2 my-2">
          <div className="col-span-1 flex items-center justify-center">
            <span className="w-3 h-3 bg-green-500 rounded-full"></span>
          </div>
          <select
            className="col-span-3 border border-black rounded-md px-3 py-2 bg-white text-black"
            value={locationCity}
            onChange={(e) => setLocationCity(e.target.value as GBACity)}
            disabled={orderCreated}
          >
            {Object.entries(GBACity).map(([key, value]) => (
              <option key={key} value={value as string}>
                {t(value)}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder={t('BookingLocationInput')}
            disabled={orderCreated}
            className={`col-span-7 rounded-md px-3 py-2 bg-white text-black border-2 text-sm ${
              locationEmpty ? "border-red-500" : "border-black"
            }`}
          />
          <div
            className="flex items-center justify-center text-white cursor-help relative"
            onMouseEnter={() => setShowLocationTooltip(true)}
            onMouseLeave={() => setShowLocationTooltip(false)}
          >
            <FaQuestionCircle size={18} className="text-green-500" />
            {showLocationTooltip && (
              <div className="absolute bottom-full mb-2 right-0 w-60 bg-gray-800 text-white text-sm p-3 rounded-md shadow-lg z-50">
                {t('BookingLocationHelpText')}
                <div className="absolute bottom-[-6px] right-1 h-3 w-3 bg-gray-800 transform rotate-45"></div>
              </div>
            )}
          </div>
        </div>
        {locationEmpty && (
          <p className="text-red-500 text-sm text-center">
            {t('BookingLocationEmptyError')}
          </p>
        )}
        <div className="grid grid-cols-12 gap-2 my-2">
          <div className="col-span-1 flex items-center justify-center">
            <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
          </div>
          <select
            className="col-span-3 border border-black rounded-md px-3 py-2 bg-white text-black"
            value={destinationCity}
            onChange={(e) => setDestinationCity(e.target.value as GBACity)}
            disabled={orderCreated}
          >
            {Object.entries(GBACity).map(([key, value]) => (
              <option key={key} value={value as string}>
                {t(value)}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder={t('BookingDestinationInput')}
            disabled={orderCreated}
            className={`col-span-7 rounded-md px-3 py-2 bg-white text-black border-2 text-sm ${
              destinationEmpty ? "border-red-500" : "border-black"
            }`}
          />
          <div
            className="flex items-center justify-center text-white cursor-help relative"
            onMouseEnter={() => setShowDestinationTooltip(true)}
            onMouseLeave={() => setShowDestinationTooltip(false)}
            ref={tooltipRef}
          >
            <FaQuestionCircle size={18} className="text-orange-500" />
            {showDestinationTooltip && (
              <div className="absolute top-full mt-2 right-0 w-60 bg-gray-800 text-white text-sm p-3 rounded-md shadow-lg z-50">
                {t('BookingDestinationHelpText')}
                <div className="absolute top-[-6px] right-1 h-3 w-3 bg-gray-800 transform rotate-45"></div>
              </div>
            )}
          </div>
        </div>
        {destinationEmpty && (
          <p className="text-red-500 text-sm text-center">
            {t('BookingDestinationEmptyError')}
          </p>
        )}
      </div>
      
      {!orderCreated ? (
        <div>
          {/* Datetime picker */}
          <div className="space-y-4 my-4">
            <label className="block w-full text-sm font-bold text-white">{t('BookingPickupTime')}</label>
            <input
              type="datetime-local"
              value={pickupTime}
              onChange={(e) => onPickupTimeChange(e.target.value)}
              disabled={orderCreated}
              className={`w-full rounded px-4 py-2 focus:outline-none focus:ring-2 text-black box-border text-left ${
                pickupTimeError ? "border-red-500 focus:ring-red-500" : "border-gray-200"
              }`}
              style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
            />
            {pickupTimeError && (
              <p className="text-red-500 text-sm">{pickupTimeError}</p>
            )}
          </div>
          <button
            onClick={handleCreateOrder}
            className={`w-full px-6 py-3 rounded-full ${
              loadingCreateOrder ? 
                "bg-gray-500 cursor-not-allowed" : 
                "bg-orange-500 hover:bg-orange-600"
            } text-white font-medium`}
            disabled={loadingCreateOrder}
          >
            {loadingCreateOrder ? (
              <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                {t('AuthProcessing')}
            </span>
            ) : (
              <span className="flex items-center justify-center">
                {t('BookingCreateOrder')}
              </span>
            )}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Pick up time text */}
          <div className="my-4">
            <label className="block w-full text-sm font-bold text-white">{t('BookingPickupTime')}</label>
            <p> {pickupTime} </p>
          </div>
          {/*Trip costs*/}
          <p className="text-white font-bold flex items-center justify-between">
            <span>
              {t('BookingRouteCost', {
                cost: showHKD ?
                  (rates.HKD !== null ? `$ ${rates.HKD}` : '') :
                  (rates.CNY !== null ? `¥ ${rates.CNY}` : '')
              })}
            </span>
            {rates.HKD !== null && rates.CNY !== null && (
              <span className="cursor-pointer text-sm ml-auto" onClick={() => setShowHKD(!showHKD)}>
                <span className={showHKD ? "font-bold text-orange-500" : "font-normal"}>HKD</span>
                <span className="mx-0.5">/</span>
                <span className={showHKD ? "font-normal" : "font-bold text-orange-500"}>CNY</span>
              </span>
            )}
          </p>
          {/* Number of Passengers picker */}
          <label className="block text-sm font-bold text-white">{t('BookingNumberOfPassengers')}</label>
          <select
            value={passengers}
            onChange={(e) => onPassengersChange(Number(e.target.value))}
            className="w-full pl-5 pr-4 py-3 rounded-full focus:outline-none focus:ring-2 text-black border border-gray-200"
          >
            {[1, 2, 3, 4, 5, 6].map(num => (
              <option key={num} value={num}>
                {num === 1
                  ? t('BookingPersonSingular', { number: num })
                  : t('BookingPersonPlural', { number: num })}
              </option>
            ))}
          </select>

          {/* Number of Luggage picker */}
          <label className="block text-sm font-bold text-white">{t('BookingNumberOfLuggage')}</label>
          <select
            value={luggage}
            onChange={(e) => onLuggageChange(Number(e.target.value))}
            className="w-full pl-5 pr-4 py-3 rounded-full focus:outline-none focus:ring-2 text-black border border-gray-200"
          >
            {[0, 1, 2, 3, 4, 5].map(num => (
              <option key={num} value={num}>{num}</option>
            ))}
          </select>
          <div>
            <button
              className="w-full py-2 mt-4 rounded-full bg-orange-500 text-white font-medium"
              onClick={onConfirmOrder}
            >
              {t('BookingConfirmOrder')}
            </button>
            <button
              className="w-full py-2 mt-4 rounded-full bg-[#181C3A] text-white font-medium"
              onClick={onCancelOrder}
            >
              {t('BookingCancelOrder')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default BookingForm;
